import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { z } from 'zod';
import { useLogin } from '../hooks/auth-actions.js';
import { describeError, isErrorCode, mergeFormClasses } from './shared.js';
const schema = z.object({
    email: z.string().trim().min(1, 'Enter your email.').email('Enter a valid email.'),
    password: z.string().min(1, 'Enter your password.'),
});
/**
 * Email + password login form. Submits, then either calls `onSuccess` for a
 * normal login or `onMfaRequired` when the server says an additional factor is
 * needed. Server errors are surfaced inline; field-level codes
 * (`INVALID_CREDENTIALS`) are flagged on the password input as well.
 */
export function LoginForm(props) {
    const { onSuccess, onMfaRequired, onUseOtpInstead, onForgotPassword, submitLabel = 'Sign in', } = props;
    const classes = mergeFormClasses(props.classNames);
    const login = useLogin();
    const [email, setEmail] = React.useState('');
    const [password, setPassword] = React.useState('');
    const [validationError, setValidationError] = React.useState({});
    const serverError = describeError(login.error, props.errorMessages);
    const credentialsError = isErrorCode(login.error, 'INVALID_CREDENTIALS');
    const handleSubmit = async (event) => {
        event.preventDefault();
        const parsed = schema.safeParse({ email, password });
        if (!parsed.success) {
            const fieldErrors = parsed.error.flatten().fieldErrors;
            setValidationError({
                email: fieldErrors.email?.[0],
                password: fieldErrors.password?.[0],
            });
            return;
        }
        setValidationError({});
        try {
            const outcome = await login.run(parsed.data);
            if (outcome.status === 'authenticated') {
                onSuccess(outcome.result);
            }
            else {
                onMfaRequired(outcome.challenge);
            }
        }
        catch {
            // Error captured by the hook; rendered inline below.
        }
    };
    return (_jsxs("form", { className: classes.form, onSubmit: handleSubmit, noValidate: true, children: [_jsxs("div", { className: classes.field, children: [_jsx("label", { className: classes.label, htmlFor: "auth-ui-login-email", children: "Email" }), _jsx("input", { id: "auth-ui-login-email", className: classes.input, type: "email", autoComplete: "email", required: true, value: email, onChange: (event) => setEmail(event.target.value), "aria-invalid": validationError.email !== undefined ? 'true' : undefined, "aria-describedby": validationError.email !== undefined ? 'auth-ui-login-email-error' : undefined }), validationError.email !== undefined ? (_jsx("p", { id: "auth-ui-login-email-error", className: classes.error, role: "alert", children: validationError.email })) : null] }), _jsxs("div", { className: classes.field, children: [_jsx("label", { className: classes.label, htmlFor: "auth-ui-login-password", children: "Password" }), _jsx("input", { id: "auth-ui-login-password", className: classes.input, type: "password", autoComplete: "current-password", required: true, value: password, onChange: (event) => setPassword(event.target.value), "aria-invalid": validationError.password !== undefined || credentialsError ? 'true' : undefined, "aria-describedby": validationError.password !== undefined
                            ? 'auth-ui-login-password-error'
                            : credentialsError
                                ? 'auth-ui-login-server-error'
                                : undefined }), validationError.password !== undefined ? (_jsx("p", { id: "auth-ui-login-password-error", className: classes.error, role: "alert", children: validationError.password })) : null] }), serverError !== null ? (_jsx("p", { id: "auth-ui-login-server-error", className: classes.error, role: "alert", children: serverError })) : null, _jsx("button", { type: "submit", className: classes.button, disabled: login.loading, children: login.loading ? 'Signing in…' : submitLabel }), onUseOtpInstead !== undefined ? (_jsx("button", { type: "button", className: classes.link, onClick: onUseOtpInstead, children: "Send me a one-time code instead" })) : null, onForgotPassword !== undefined ? (_jsx("button", { type: "button", className: classes.link, onClick: onForgotPassword, children: "Forgot password?" })) : null] }));
}
//# sourceMappingURL=LoginForm.js.map